import * as ROUTES from 'constants/routes'
import { Tooltip } from '@mui/material'
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined'
import DeepcellPrimaryButton from 'components/shared/DeepcellPrimaryButton'
import { generatePath, useHistory } from 'react-router-dom'
import useCellVisualizationUrlParams from '../useCellVisualizationUrlParams'
import { useLabelingEligibility } from './useLabelingEligibility'

/**
 * Top-bar entry into the internal labeling workflow. Rendered only for internal
 * users (gated by the caller). Enabled only when the session is eligible
 * (single run, 100% of cells); otherwise shown disabled with an explanatory
 * tooltip so the completeness requirement is taught rather than hidden.
 */
export const LabelImagesButton = (): JSX.Element => {
  const history = useHistory()
  const { sessionId } = useCellVisualizationUrlParams()
  const { canLabel, reason, loading } = useLabelingEligibility()

  const disabled = !canLabel
  const tooltipTitle = loading
    ? 'Checking whether this session can be labeled…'
    : reason ?? ''

  const goToLabeling = () => {
    if (!sessionId) return
    history.push(generatePath(ROUTES.CELL_VISUALIZATIONS_SESSION_LABEL, { sessionId: String(sessionId) }))
  }

  const button = (
    <DeepcellPrimaryButton
      contained
      disabled={disabled}
      startIcon={<LabelOutlinedIcon sx={{ fill: (t) => t.palette.primary.main }} />}
      onClick={goToLabeling}
      data-testid="label-images-button"
    >
      Label Images
    </DeepcellPrimaryButton>
  )

  // A disabled MUI button does not emit hover events, so wrap it in a span for
  // the tooltip to work. Only show a tooltip when there is something to say.
  return tooltipTitle ? (
    <Tooltip title={tooltipTitle}>
      <span>{button}</span>
    </Tooltip>
  ) : (
    button
  )
}

export default LabelImagesButton
